import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeFilter.css';

const DateRangeFilter = ({ startDate, endDate, onStartDateChange, onEndDateChange, onApply, defaultDays = 30, earliestDate, latestDate, showMax = true }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(defaultDays);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const hasMounted = useRef(false);

  // Enable transitions only after first paint
  useEffect(() => {
    requestAnimationFrame(() => {
      hasMounted.current = true;
    });
  }, []);

  // Sync local state with props when they change from parent
  useEffect(() => {
    if (selectedPeriod !== 'max') {
      setLocalStartDate(startDate);
      setLocalEndDate(endDate);
    }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  // When in Max mode, update pickers from earliestDate once it arrives; end date stays as today
  useEffect(() => {
    if (selectedPeriod === 'max') {
      if (earliestDate) setLocalStartDate(earliestDate.split('T')[0]);
      setLocalEndDate(toUTCDateStr(new Date()));
    }
  }, [selectedPeriod, earliestDate]);

  // Check if current date range matches a quick select period
  useEffect(() => {
    if (selectedPeriod === 'max') return;
    if (startDate && endDate) {
      const today = new Date();
      const todayStr = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`;

      const endDateObj = new Date(endDate + 'T00:00:00');
      const startDateObj = new Date(startDate + 'T00:00:00');

      const daysDiff = Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
      const endDateIsToday = endDate === todayStr;

      if (endDateIsToday && daysDiff === 6) {
        setSelectedPeriod(7);
      } else if (endDateIsToday && daysDiff === 29) {
        setSelectedPeriod(30);
      } else if (endDateIsToday && daysDiff === 89) {
        setSelectedPeriod(90);
      } else if (selectedPeriod !== 'max') {
        setSelectedPeriod(null);
      }
    } else if (selectedPeriod !== 'max') {
      setSelectedPeriod(null);
    }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  const handleApply = () => {
    if (localStartDate && localEndDate) {
      onStartDateChange(localStartDate);
      onEndDateChange(localEndDate);
      onApply(localStartDate, localEndDate);
    }
  };

  const toUTCDateStr = (date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const applyDays = (days) => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - (days - 1));

    const newStartDate = toUTCDateStr(start);
    const newEndDate = toUTCDateStr(end);

    setSelectedPeriod(days);
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);
    onStartDateChange(newStartDate);
    onEndDateChange(newEndDate);
    onApply(newStartDate, newEndDate);
  };

  const handleQuickSelect = (days) => {
    if (selectedPeriod === days) {
      applyDays(defaultDays);
    } else {
      applyDays(days);
    }
  };

  const handleMaxSelect = () => {
    if (selectedPeriod === 'max') {
      // Deselect: reset to default period
      applyDays(defaultDays);
    } else {
      // Select max: send empty dates so backend returns all data
      setSelectedPeriod('max');
      // Show earliest in picker if available; end date is always today
      setLocalStartDate(earliestDate ? earliestDate.split('T')[0] : '');
      setLocalEndDate(toUTCDateStr(new Date()));
      // Send empty dates to parent — backend skips date filtering when empty
      onStartDateChange('');
      onEndDateChange('');
      onApply('', '');
    }
  };

  const handleDateChange = (setter, value) => {
    setSelectedPeriod(null);
    setter(value);
  };

  const parseDate = (dateStr) => {
    return dateStr ? new Date(dateStr + 'T00:00:00') : null;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const btnStyle = !hasMounted.current ? { transition: 'none' } : undefined;

  return (
    <div className="date-range-filter">
      <div className="date-inputs">
        <div className="date-input-group">
          <label>From:</label>
          <DatePicker
            selected={parseDate(localStartDate)}
            onChange={(date) => handleDateChange(setLocalStartDate, formatDate(date))}
            dateFormat="yyyy-MM-dd"
            maxDate={localEndDate ? parseDate(localEndDate) : new Date()}
            placeholderText="yyyy-mm-dd"
            className="custom-date-input"
          />
        </div>

        <div className="date-input-group">
          <label>To:</label>
          <DatePicker
            selected={parseDate(localEndDate)}
            onChange={(date) => handleDateChange(setLocalEndDate, formatDate(date))}
            dateFormat="yyyy-MM-dd"
            minDate={localStartDate ? parseDate(localStartDate) : null}
            maxDate={new Date()}
            placeholderText="yyyy-mm-dd"
            className="custom-date-input"
          />
        </div>

        <button className="btn-primary" onClick={handleApply}>
          Apply
        </button>
      </div>

      <div className="quick-select">
        <span className="quick-label">Quick Select:</span>
        <button
          onClick={() => handleQuickSelect(7)}
          className={`quick-btn ${selectedPeriod === 7 ? 'active' : ''}`}
          style={btnStyle}
        >
          7 Days
        </button>
        <button
          onClick={() => handleQuickSelect(30)}
          className={`quick-btn ${selectedPeriod === 30 ? 'active' : ''}`}
          style={btnStyle}
        >
          30 Days
        </button>
        <button
          onClick={() => handleQuickSelect(90)}
          className={`quick-btn ${selectedPeriod === 90 ? 'active' : ''}`}
          style={btnStyle}
        >
          90 Days
        </button>
        {showMax && (
          <button
            onClick={handleMaxSelect}
            className={`quick-btn ${selectedPeriod === 'max' ? 'active' : ''}`}
            style={btnStyle}
          >
            Max
          </button>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
